import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useToast } from '../contexts/ToastContext';

const JobSearch = ({ 
    onSearch,
    // City integration props
    cityOptions = [],
    cityQuery = '',
    setCityQuery = () => {},
    showCityDropdown = false,
    setShowCityDropdown = () => {},
    isCityLoading = false,
    isAddingCity = false,
    selectedCities = [],
    addCity = () => {},
    removeCity = () => {},
    clearAllCities = () => {},
    cityInputRef,
    // Skills
    skillsOptions = [],
    selectedSkills = [],
    addSkill = () => {},
    removeSkill = () => {},
    clearAllSkills = () => {},
    // Designations (keywords)
    selectedDesignations = [],
    addDesignation = () => {},
    removeDesignation = () => {},
    clearAllDesignations = () => {},
    // Designation options from API
    designationOptions = [],
    // Keywords (free-text search terms)
    selectedKeywords = [],
    addKeyword = () => {},
    removeKeyword = () => {},
    clearAllKeywords = () => {},
    // Clear all filters
    clearAllFilters = () => {},
    // Disable Search button when jobs API is in-flight
    isLoadingJobs = false
}) => {
    const { showToast } = useToast();
    const [location, setLocation] = useState('');
    const [filterInput, setFilterInput] = useState(''); // Unified input for skills, designations, keywords
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const filterContainerRef = useRef(null);
    const filterInputRef = useRef(null);
    const locationContainerRef = useRef(null);
    
    // Responsive: track window width for mobile layout
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Click outside handler for location dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (locationContainerRef.current && !locationContainerRef.current.contains(event.target)) {
                setShowCityDropdown(false);
            }
        };
        
        if (showCityDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showCityDropdown, setShowCityDropdown]);

    const handleSearch = () => {
        // Validation: Check if at least one search field has input OR chips exist
        const hasChips = (selectedCities?.length || 0) > 0 || 
                        (selectedSkills?.length || 0) > 0 || 
                        (selectedDesignations?.length || 0) > 0 ||
                        (selectedKeywords?.length || 0) > 0;
        
        if (!hasChips) {
            showToast('Please enter at least one search criteria (location, job title, skills, or keywords) to search for jobs.', 'warning');
            return;
        }
        
        console.log('Searching with chips:', { 
            cities: selectedCities, 
            skills: selectedSkills, 
            designations: selectedDesignations,
            keywords: selectedKeywords 
        });
        
        // Call the parent's onSearch callback if provided
        if (onSearch) {
            onSearch({});
        }
    };

    // Combined suggestions from skills and designations
    const combinedSuggestions = useMemo(() => {
        const suggestions = [];
        
        // Add skills with type marker
        (skillsOptions || []).forEach(opt => {
            const label = typeof opt === 'object' 
                ? (opt.skillName || opt.name || opt.label || opt.skill || opt.title || opt.skill_name || '')
                : String(opt);
            if (label && !selectedSkills.includes(label)) {
                suggestions.push({ label: label.trim(), type: 'skill', raw: opt });
            }
        });
        
        // Add designations with type marker
        (designationOptions || []).forEach(opt => {
            const label = typeof opt === 'object'
                ? (opt.designation || opt.name || opt.label || opt.title || '')
                : String(opt);
            if (label && !selectedDesignations.includes(label)) {
                suggestions.push({ label: label.trim(), type: 'designation', raw: opt });
            }
        });
        
        return suggestions;
    }, [skillsOptions, designationOptions, selectedSkills, selectedDesignations]);

    // Filter suggestions based on input
    const filteredSuggestions = useMemo(() => {
        const query = (filterInput || '').toLowerCase().trim();
        if (!query) return combinedSuggestions.slice(0, 20);
        return combinedSuggestions
            .filter(s => s.label.toLowerCase().includes(query))
            .slice(0, 20);
    }, [filterInput, combinedSuggestions]);

    // Close filter dropdown on outside click/touch
    useEffect(() => {
        const handleOutside = (e) => {
            if (!filterContainerRef.current) return;
            if (!filterContainerRef.current.contains(e.target)) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, []);

    // Handle adding a filter item (skill, designation, or keyword)
    const handleAddFilterItem = (item) => {
        if (item.type === 'skill') {
            addSkill(item.label);
        } else if (item.type === 'designation') {
            addDesignation(item.label);
        }
        setFilterInput('');
        setShowFilterDropdown(false);
        filterInputRef.current?.blur();
    };

    // Handle adding free-text keyword (when Enter or comma pressed and no matching suggestion)
    const handleAddKeyword = (text) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        
        // Check if it matches any suggestion
        const matchingSuggestion = combinedSuggestions.find(
            s => s.label.toLowerCase() === trimmed.toLowerCase()
        );
        
        if (matchingSuggestion) {
            // Add as skill or designation
            handleAddFilterItem(matchingSuggestion);
        } else {
            // Add as free-text keyword (if not already added)
            if (!selectedKeywords.includes(trimmed)) {
                addKeyword(trimmed);
            }
        }
        setFilterInput('');
        setShowFilterDropdown(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const v = (filterInput || '').trim();
            if (v) {
                handleAddKeyword(v);
            } else {
                handleSearch();
            }
        } else if (e.key === ',') {
            e.preventDefault();
            const v = (filterInput || '').trim();
            if (v) {
                handleAddKeyword(v);
            }
        }
    };

    const sectionStyle = {
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)',
        color: 'white',
        padding: '2.5rem 0',
        position: 'relative',
        overflow: 'visible'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.875rem 0.875rem 0.875rem 2.75rem',
        border: 'none',
        borderRadius: '0',
        fontSize: '0.9rem',
        color: '#374151',
        transition: 'all 0.3s ease',
        background: 'transparent',
        outline: 'none'
    };

    const iconStyle = {
        position: 'absolute',
        left: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#3b82f6',
        fontSize: '1rem'
    };

    // Count of filter chips for placeholder logic
    const totalFilterChips = (selectedSkills?.length || 0) + (selectedDesignations?.length || 0) + (selectedKeywords?.length || 0);

    return (
        <section style={sectionStyle}>
            <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ textAlign: 'center', margin: 0, marginBottom: '1.5rem' }}>
                    <h1 style={{ fontSize: isMobile ? '1.75rem' : '2.25rem', margin: 0, fontWeight: 700, lineHeight: 1.2 }}>
                        Find Your Dream Job
                    </h1>
                </div>
                
                {/* Desktop Layout - horizontal pill */}
                {!isMobile && (
                    <div style={{ maxWidth: '900px', margin: '0 auto', background: 'white', borderRadius: '50px', padding: '0.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            {/* Location Field */}
                            <div style={{ position: 'relative', flex: '0 0 220px', paddingRight: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', height: '24px', width: '1px', background: '#e5e7eb' }} />
                                <i className="fas fa-map-marker-alt" style={iconStyle}></i>
                                <input 
                                    ref={cityInputRef}
                                    type="text" 
                                    placeholder="Location" 
                                    style={inputStyle}
                                    value={selectedCities.length > 0 ? selectedCities[0] : location}
                                    onFocus={() => setShowCityDropdown(true)}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (selectedCities.length > 0) clearAllCities();
                                        setLocation(v);
                                        setCityQuery(v);
                                        setShowCityDropdown(true);
                                    }}
                                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                                />
                                {isAddingCity && (
                                    <span title="Adding city..." style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                )}
                                {showCityDropdown && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.1)', zIndex: 1000, maxHeight: 240, overflowY: 'auto' }}>
                                        {isCityLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.8rem' }}>
                                                <span className="spinner" style={{ width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                                Loading suggestions…
                                            </div>
                                        ) : (
                                            <>
                                                {cityOptions.length > 0 ? (
                                                    cityOptions.slice(0, 50).map((opt, idx) => {
                                                        const isObj = typeof opt === 'object' && opt !== null;
                                                        const label = isObj ? (opt.label || opt.name || opt.description || '') : String(opt);
                                                        const keyVal = isObj ? (opt.placeId || opt.place_id || label) : label;
                                                        return (
                                                            <div key={`js-city-opt-${keyVal}-${idx}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { addCity(isObj ? opt : label); setLocation(''); setCityQuery(''); setShowCityDropdown(false); }} style={{ padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.8rem', color: '#111827', borderBottom: '1px solid #f1f5f9' }} className="city-option">
                                                                {label}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    cityQuery?.trim() ? (
                                                        <div style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem' }}>No results found</div>
                                                    ) : (
                                                        <div style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem' }}>Type to search locations…</div>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Filter Field with chips */}
                            <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }} ref={filterContainerRef}>
                                <i className="fas fa-search" style={{ color: '#3b82f6', fontSize: '1rem', flexShrink: 0, marginLeft: '0.5rem', marginRight: '0.5rem' }}></i>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.75rem 0.5rem 0', border: 'none', borderRadius: '0', background: 'transparent', minHeight: '48px', cursor: 'text', overflowX: 'scroll', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', flex: 1 }} className="hide-scrollbar" onClick={() => filterInputRef?.current?.focus()}>
                                    {selectedDesignations.map((dg) => (
                                        <span key={`inline-dg-${dg}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {dg}
                                            <button onClick={(e) => { e.stopPropagation(); removeDesignation(dg); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, marginLeft: '0.15rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="fas fa-times" style={{ fontSize: '0.6rem' }}></i>
                                            </button>
                                        </span>
                                    ))}
                                    {selectedSkills.map((skill) => (
                                        <span key={`inline-skill-${skill}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {skill}
                                            <button onClick={(e) => { e.stopPropagation(); removeSkill(skill); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, marginLeft: '0.15rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="fas fa-times" style={{ fontSize: '0.6rem' }}></i>
                                            </button>
                                        </span>
                                    ))}
                                    {(selectedKeywords || []).map((kw) => (
                                        <span key={`inline-kw-${kw}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {kw}
                                            <button onClick={(e) => { e.stopPropagation(); removeKeyword(kw); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, marginLeft: '0.15rem', display: 'flex', alignItems: 'center' }}>
                                                <i className="fas fa-times" style={{ fontSize: '0.6rem' }}></i>
                                            </button>
                                        </span>
                                    ))}
                                    <input ref={filterInputRef} type="text" placeholder={totalFilterChips === 0 ? "Job title, skills, keywords..." : ""} style={{ flex: 1, minWidth: '100px', border: 'none', outline: 'none', fontSize: '0.9rem', color: '#374151', background: 'transparent', padding: '0.25rem 0' }} value={filterInput} onFocus={() => { if (filterInput.trim()) setShowFilterDropdown(true); }} onChange={(e) => { setFilterInput(e.target.value); if (e.target.value.trim()) setShowFilterDropdown(true); else setShowFilterDropdown(false); }} onKeyDown={handleKeyPress} />
                                </div>
                                {showFilterDropdown && filterInput.trim() && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.1)', zIndex: 1000, maxHeight: 280, overflowY: 'auto' }} onMouseDown={(e) => e.preventDefault()}>
                                        {filteredSuggestions.length > 0 ? (
                                            <>
                                                {filteredSuggestions.map((item, idx) => (
                                                    <div key={`js-filter-${item.type}-${item.label}-${idx}`} onClick={() => handleAddFilterItem(item)} style={{ padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.8rem', color: '#111827', borderBottom: '1px solid #f1f5f9' }} className="filter-option">
                                                        {item.label}
                                                    </div>
                                                ))}
                                            </>
                                        ) : null}
                                    </div>
                                )}
                            </div>

                            {/* Search Button */}
                            <button onClick={handleSearch} disabled={isAddingCity || isLoadingJobs} style={{ background: (isAddingCity || isLoadingJobs) ? '#9ca3af' : '#2563eb', color: 'white', padding: '0.875rem 1.5rem', border: 'none', borderRadius: '50px', fontWeight: 600, cursor: (isAddingCity || isLoadingJobs) ? 'not-allowed' : 'pointer', fontSize: '0.9rem', transition: 'all 0.3s ease', whiteSpace: 'nowrap', opacity: (isAddingCity || isLoadingJobs) ? 0.8 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }} onMouseOver={e => { if (!(isAddingCity || isLoadingJobs)) e.currentTarget.style.background='#1d4ed8' }} onMouseOut={e => { e.currentTarget.style.background=(isAddingCity || isLoadingJobs) ? '#9ca3af' : '#2563eb' }}>
                                <i className="fas fa-search"></i>
                                {isAddingCity ? 'Adding…' : (isLoadingJobs ? 'Searching…' : 'Search')}
                            </button>

                            {/* Reset Button */}
                            <button onClick={clearAllFilters} title="Reset all filters" style={{ background: '#f1f5f9', color: '#64748b', padding: '0.875rem 1.25rem', border: 'none', borderRadius: '50px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s ease', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }} onMouseOver={e => { e.currentTarget.style.background='#e2e8f0'; }} onMouseOut={e => { e.currentTarget.style.background='#f1f5f9'; }}>
                                <i className="fas fa-redo-alt"></i>
                                Reset
                            </button>
                        </div>
                    </div>
                )}

                {/* Mobile Layout - stacked vertically */}
                {isMobile && (
                    <div style={{ maxWidth: '100%', margin: '0 1rem', background: 'white', borderRadius: '16px', padding: '1rem', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                        {/* Location Field */}
                        <div style={{ position: 'relative', marginBottom: '0.5rem' }} ref={locationContainerRef}>
                            <div style={{ display: 'flex', alignItems: 'center', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '0.5rem 0.75rem' }}>
                                <i className="fas fa-map-marker-alt" style={{ color: '#3b82f6', fontSize: '1rem', marginRight: '0.75rem' }}></i>
                                <input 
                                    type="text" 
                                    placeholder="Location" 
                                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.9rem', color: '#374151', background: 'transparent', padding: '0.25rem 0', boxShadow: 'none' }}
                                    value={selectedCities.length > 0 ? selectedCities[0] : cityQuery}
                                    onFocus={() => setShowCityDropdown(true)}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (selectedCities.length > 0) clearAllCities();
                                        setCityQuery(v);
                                        setShowCityDropdown(true);
                                    }}
                                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                                    className="mobile-search-input"
                                />
                                {isAddingCity && (
                                    <span style={{ width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                )}
                            </div>
                            {showCityDropdown && (
                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.1)', zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}>
                                    {isCityLoading ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.8rem' }}>
                                            <span className="spinner" style={{ width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            Loading…
                                        </div>
                                    ) : (
                                        <>
                                            {cityOptions.length > 0 ? (
                                                cityOptions.slice(0, 30).map((opt, idx) => {
                                                    const isObj = typeof opt === 'object' && opt !== null;
                                                    const label = isObj ? (opt.label || opt.name || opt.description || '') : String(opt);
                                                    const keyVal = isObj ? (opt.placeId || opt.place_id || label) : label;
                                                    return (
                                                        <div key={`js-city-mob-${keyVal}-${idx}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { addCity(isObj ? opt : label); setLocation(''); setCityQuery(''); setShowCityDropdown(false); }} style={{ padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.85rem', color: '#111827', borderBottom: '1px solid #f1f5f9' }} className="city-option">
                                                            {label}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                cityQuery?.trim() ? (
                                                    <div style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem' }}>No results found</div>
                                                ) : (
                                                    <div style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem' }}>Type to search locations…</div>
                                                )
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Filter Field with chips */}
                        <div style={{ position: 'relative', marginBottom: '0.5rem' }} ref={filterContainerRef}>
                            <div style={{ display: 'flex', alignItems: 'center', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '0.5rem 0.75rem', minHeight: '40px' }}>
                                <i className="fas fa-search" style={{ color: '#3b82f6', fontSize: '1rem', marginRight: '0.75rem', flexShrink: 0 }}></i>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }} className="hide-scrollbar" onClick={() => filterInputRef?.current?.focus()}>
                                    {selectedDesignations.map((dg) => (
                                        <span key={`mob-dg-${dg}`} style={{ display: 'inline-flex', alignItems: 'center', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '2px 5px 2px 9px', borderRadius: '9999px', fontSize: '0.85rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            <span style={{ lineHeight: 1 }}>{dg}</span>
                                            <button onClick={(e) => { e.stopPropagation(); removeDesignation(dg); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '5px' }}>
                                                <i className="fas fa-times" style={{ fontSize: '0.65rem' }}></i>
                                            </button>
                                        </span>
                                    ))}
                                    {selectedSkills.map((skill) => (
                                        <span key={`mob-skill-${skill}`} style={{ display: 'inline-flex', alignItems: 'center', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '2px 5px 2px 9px', borderRadius: '9999px', fontSize: '0.85rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            <span style={{ lineHeight: 1 }}>{skill}</span>
                                            <button onClick={(e) => { e.stopPropagation(); removeSkill(skill); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '5px' }}>
                                                <i className="fas fa-times" style={{ fontSize: '0.65rem' }}></i>
                                            </button>
                                        </span>
                                    ))}
                                    {(selectedKeywords || []).map((kw) => (
                                        <span key={`mob-kw-${kw}`} style={{ display: 'inline-flex', alignItems: 'center', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '2px 5px 2px 9px', borderRadius: '9999px', fontSize: '0.85rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            <span style={{ lineHeight: 1 }}>{kw}</span>
                                            <button onClick={(e) => { e.stopPropagation(); removeKeyword(kw); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '5px' }}>
                                                <i className="fas fa-times" style={{ fontSize: '0.65rem' }}></i>
                                            </button>
                                        </span>
                                    ))}
                                    <input ref={filterInputRef} type="text" placeholder={totalFilterChips === 0 ? "Job title, skills, keywords..." : ""} style={{ flex: 1, minWidth: '80px', border: 'none', outline: 'none', fontSize: '0.9rem', color: '#374151', background: 'transparent', padding: '0.25rem 0', boxShadow: 'none' }} value={filterInput} onFocus={() => { if (filterInput.trim()) setShowFilterDropdown(true); }} onChange={(e) => { setFilterInput(e.target.value); if (e.target.value.trim()) setShowFilterDropdown(true); else setShowFilterDropdown(false); }} onKeyDown={handleKeyPress} className="mobile-search-input" />
                                </div>
                            </div>
                            {showFilterDropdown && filterInput.trim() && (
                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.1)', zIndex: 1000, maxHeight: 200, overflowY: 'auto' }} onMouseDown={(e) => e.preventDefault()}>
                                    {filteredSuggestions.length > 0 ? (
                                        <>
                                            {filteredSuggestions.map((item, idx) => (
                                                <div key={`js-mob-filter-${item.type}-${item.label}-${idx}`} onClick={() => handleAddFilterItem(item)} style={{ padding: '0.6rem 1rem', cursor: 'pointer', fontSize: '0.85rem', color: '#111827', borderBottom: '1px solid #f1f5f9' }} className="filter-option">
                                                    {item.label}
                                                </div>
                                            ))}
                                        </>
                                    ) : null}
                                </div>
                            )}
                        </div>

                        {/* Buttons Row - Reset and Search side by side */}
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            {/* Reset Button */}
                            <button onClick={clearAllFilters} title="Reset all filters" style={{ flex: 1, background: '#f1f5f9', color: '#64748b', padding: '0.875rem 1rem', border: 'none', borderRadius: '12px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <i className="fas fa-redo-alt"></i>
                                Reset
                            </button>

                            {/* Search Button */}
                            <button onClick={handleSearch} disabled={isAddingCity || isLoadingJobs} style={{ flex: 1, background: (isAddingCity || isLoadingJobs) ? '#9ca3af' : '#2563eb', color: 'white', padding: '0.875rem 1rem', border: 'none', borderRadius: '12px', fontWeight: 600, cursor: (isAddingCity || isLoadingJobs) ? 'not-allowed' : 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <i className="fas fa-search"></i>
                                {isAddingCity ? 'Adding…' : (isLoadingJobs ? 'Searching…' : 'Search')}
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </section>
    );
}

export default JobSearch;
